import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
/* eslint-disable react/no-danger */
import moment from 'moment';
import t from 'react-translate';

// Hooks
import useSessionStates from 'shared/hooks/use-session-states';
import { useAppDispatch } from 'redux/store';

// Actions
import { toggleSessionRegistration } from 'redux/actions/live-events';

// Styles
import { css } from '@emotion/react';
import { quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { mobile } from 'styles/global_defaults/media-queries';
import { danger, gray3, gray4, gray6 } from 'styles/global_defaults/colors';

// Hooks
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

// Components & Types
import { AttendeeInfoStatus, LiveEventSession } from 'redux/schemas/models/live-event';
import NvVideoPreview, { NvVideoPreviewImperativeInterface } from 'shared/components/nv-video-preview';
import NvFileUploadProgress from 'shared/components/nv-file-upload-progress';
import { FileUploading } from 'shared/hooks/use-upload-file';
import { AngularContext } from 'react-app';
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import ClickableContainer from 'components/clickable-container';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import NvIcon from 'shared/components/nv-icon';
import LiveSessionCardAction, { LiveSessionState } from './live-session-card-action';
import LiveSessionCardState, { CardStatus } from './live-session-card-state';
import LiveSessionCardTitle, { CardBadge } from './live-session-card-title';
import LiveSessionCardEditOptions from './live-session-card-edit-options';
import RegisteredDetailsModal from './registered-details-modal';

// Pendo
import { config } from '../../../../config/pendo.config.json';
import { LiveSessionsContext } from '../live-session-lecture-component';

const DEFAULT_PROGRESS = 20;

const styles = css`
  justify-content: space-between;
  border: 1px solid ${gray4};
  border-radius: ${quarterSpacing}px;
  flex-direction: row;
  gap: ${standardSpacing}px;
  word-break: break-all;
  overflow-wrap: anywhere;

  &.video-expanded {
    flex-direction: column;
    width: 100%;
    .session-recording {
      flex-direction: column;
    }
  }
  &.border-dashed {
    border: 1px dashed ${danger};
  }

  &.video-compact {
    flex-direction: row;
    .session-recording {
      min-width: 240px;
      height: 150px;
      &.uploader {
        width: 240px;
        .upload-text {
          justify-content: space-between;
          display: flex;
        }
      }
      .nv-video-preview {
        height: 100% !important;
        .unavailable {
          height: 100% !important;
        }

        video {
          height: 100%;
        }
      }
      .recording-header {
        display: none !important;
      }
    }
  }

  .session-recording.uploader {
    max-height: 150px;
    width: 240px;
    .file-upload-progress {
      height: calc(100% - 40px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      .bs4-progress {
        width: 100%;
      }
    }
    .upload-text {
      justify-content: center;
      display: flex;
      margin-top: 10px;
      .file-name {
        margin-top: 0;
        margin-right: 10px;
      }
    }
  }

  .session-body {
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
    height: 100%;
    .session-description {
      .froala-style-regular {
        line-height: ${standardSpacing}px;
      }
    }
    .session-title {
      .text-large-body {
        word-break: break-all;
      }
    }
  }

  .session-actions {
    flex-direction: column;
    min-width: 140px;
    .disabled {
      pointer-events: none;
    }
  }

  ${mobile(css`
    flex-direction: column !important;
    .session-body {
      .session-title {
        flex-direction: column;
        > div {
          width: 100%;
          margin-right: 0 !important;
        }
      }
    }
    .session-recording {
      max-width: unset !important;
      margin-right: 0 !important;
      .nv-video-preview {
        > div {
          max-height: 200px;
        }
      }
      .recording-header {
        display: none !important;
      }
    }

    a.disabled {
      background-color: ${gray6};
      border-color: ${gray6};
      color: ${gray3};
      pointer-events: none;
    }
  `)};
`;

const getSessionStatus = (state): CardStatus => {
  const { isRegistered, inProgress, startsSoon, ended } = state;
  if (inProgress) {
    return CardStatus.IN_PROGRESS;
  } if (startsSoon) {
    return CardStatus.STARTS_SOON;
  } if (isRegistered && !ended) {
    return CardStatus.UNREGISTER;
  } return CardStatus.REGISTER;
};

const getSessionAction = (state) => {
  const { isRegistered, inProgress, startsSoon, ended, allowUserRegistration, hasReachedLimit } = state;
  let disabled = true;
  let status: LiveSessionState = null;

  if (ended) {
    return null;
  } if (isRegistered || !allowUserRegistration) {
    status = LiveSessionState.JOIN_NOW;
    disabled = !(inProgress || startsSoon);
  } else {
    status = LiveSessionState.REGISTER;
    disabled = hasReachedLimit;
  }

  return { disabled, status };
};

const getCardBadge = (state): CardBadge => {
  const { isRegistered, hasViewed, hasAttended, isPending, missed, isAdmin } = state;

  if (hasViewed) return CardBadge.VIEWED;
  if (hasAttended) return CardBadge.ATTENDED;
  if (isPending) return CardBadge.PENDING;
  if (isRegistered && isAdmin) return null;
  if (isRegistered && missed) return CardBadge.MISSED;
  if (isRegistered) return CardBadge.REGISTERED;
  if (!isRegistered) return CardBadge.UNREGISTERED;

  return null;
};

type LiveSessionTimingProps = {
  duration: any
  startTime: string
  timezone: string
};

const LiveSessionTiming = (props: LiveSessionTimingProps) => {
  const { duration, startTime, timezone } = props;
  const formattedStartTime = moment(startTime).format('LLL');
  return (
    <div className='session-timing d-flex gray-2'>
      {startTime ? (
        <div className='session-start-time'>{formattedStartTime}</div>
      ) : (
        <div className='d-flex align-items-center'>
          <NvIcon icon='warning' size='small' className='text-danger mr-2' />
          <div className='text-danger'>{t.VALIDATION.INVALID_DATE()}</div>
        </div>
      )}
      <div className='ml-1 mr-1'>|</div>
      <div className='session-duration'>{t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.DURATION(duration)}</div>
    </div>
  );
};

export type UploadEvents = {
  onFileSelected: (file: FileUploading) => void
  onFileLoaded: (file: FileUploading) => void
  onSuccess: (recording: any) => void
  onCloseModal?: () => void
};

export enum RecordingEvent {
  PLAY = 'play',
  PAUSE = 'pause',
  ENDED = 'ended',
  READY = 'ready',
  UNLOAD = 'unload',
}

type LiveSessionCardProps = {
  session: LiveEventSession
};
const LiveSessionCard = ({ session }: LiveSessionCardProps) => {
  const {
    isEdit,
    joinNow,
    uploadAttendeeList,
    uploadRecording,
    editRecording,
    removeRecording,
    delete: deleteSession,
    recordingEventCallback,
    isRegistrationEnabled,
    lectureComponent,
    isAdmin,
    catalogId,
    onSuccessDeletion,
    registrationLimit,
  } = useContext(LiveSessionsContext);

  const {
    id,
    title,
    description,
    startTime,
    duration,
    timeZone,
    attendeeInfo,
    recording: sessionRecording,
    totalRegisteredUsersCount: totalCount,
  } = session;

  const { injectServices } = useContext(AngularContext);
  const [CurrentUserManager, ReactLecturePageContext] = injectServices(['CurrentUserManager', 'ReactLecturePageContext']);
  const localTimeZone = CurrentUserManager.user.timeZone ?? CurrentUserManager.user.browserTimeZone;

  const [isExpanded, setIsExpanded] = useState(false);
  const [videoPlayerClass, setVideoPlayerClass] = useState('');
  const [recording, setRecording] = useState(sessionRecording?.file || null);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileUploading>(null);
  const [downloadOptions, setDownloadOptions] = useState([]);
  const [showRegisteredModal, setShowRegisteredModal] = useState(false);
  const [registrationRequesting, setRegistrationrequesting] = useState(false);
  const [totalRegisteredUsersCount, setTotalRegisteredUsersCount] = useState(totalCount);

  const reachedLimit = registrationLimit != null && (registrationLimit === 0 || totalRegisteredUsersCount >= registrationLimit);
  const [hasReachedLimit, setHasReachedLimit] = useState(reachedLimit);

  const playerInstanceRef = useRef<NvVideoPreviewImperativeInterface>(null);
  const { status } = attendeeInfo || {};

  const dispatch = useAppDispatch();

  const toggleRegistration = (isRegister: boolean) => {
    const registerSession = async () => {
      setRegistrationrequesting(true);
      const result = await dispatch(toggleSessionRegistration({
        catalogId,
        sessionId: id,
        isRegister,
      }));
      if (result) {
        const { payload: { error } } = result;
        if (error?.code === 'error.live_session_attendance.registrations_limit_reached') {
          if (error?.error_code_params?.total_registered_users_count) {
            setTotalRegisteredUsersCount(error?.error_code_params.total_registered_users_count);
          }
          setHasReachedLimit(true);
        } else {
          ReactLecturePageContext.updateAngularComponentStatus('live_session', lectureComponent.id, result.payload);
        }
        setRegistrationrequesting(false);
      }
    };
    if (!isRegister) {
      dispatch(openConfirmationDialog({
        title: t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.UNREGISTER.TITLE(),
        bodyText: t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.UNREGISTER.DESCRIPTION(),
        confirmText: t.FORM.YES_SURE(),
        onConfirm: () => registerSession(),
      }));
    } else {
      registerSession();
    }
  };

  const onCardActionClicked = (state: LiveSessionState, e: MouseEvent) => {
    if (state === LiveSessionState.REGISTER) {
      e.preventDefault();
      toggleRegistration(true);
    } else joinNow(id);
  };

  const hasAttended = status === AttendeeInfoStatus.ATTENDED_AUTO || status === AttendeeInfoStatus.ATTENDED_MANUAL;
  const hasViewed = status === AttendeeInfoStatus.WATCHED_RECORDING;
  const isPending = status === AttendeeInfoStatus.PENDING;
  const missed = status === AttendeeInfoStatus.MISSED;
  const isRegistered = attendeeInfo?.register;

  const sessionSettings = useSessionStates({ startTime, duration, timezone: timeZone });
  const sessionStatus: CardStatus = getSessionStatus({ ...sessionSettings, isRegistered });
  const sessionAction = getSessionAction({
    ...sessionSettings,
    isRegistered,
    allowUserRegistration: isRegistrationEnabled && !isAdmin, // Aaction is not applicable for admins. default to 'Join'
    hasReachedLimit,
  });
  const cardBadge: CardBadge = getCardBadge({ hasAttended, hasViewed, isRegistered, isPending, missed, isAdmin });
  const shouldShowSessionStatus = sessionAction?.status && !isEdit;
  const { lecturePageId } = useLecturePageParams();

  const canShowRegisteredCount = isRegistrationEnabled && !isEdit && (isAdmin || !sessionSettings.ended);

  const onRecordingEvent = (event: RecordingEvent) => {
    if (playerInstanceRef.current) {
      const total = playerInstanceRef.current.getDuration();
      const currentTime = playerInstanceRef.current.getPosition();
      if (event === RecordingEvent.PLAY) setIsExpanded(true);
      recordingEventCallback(event, currentTime, total, lecturePageId, lectureComponent.id, id);
    }
  };

  const eventHandlers = {
    onPlay: onRecordingEvent.bind(null, RecordingEvent.PLAY),
    onPause: onRecordingEvent.bind(null, RecordingEvent.PAUSE),
    onEnded: onRecordingEvent.bind(null, RecordingEvent.ENDED),
  };

  const onCloseVideo = () => {
    setIsExpanded(false);
  };

  const onSuccessUploading = (record = null) => {
    setRecording(record?.file || null);
    if (record) setUploadingRecording(false);
  };

  const uploadEvents: UploadEvents = {
    onFileSelected: (file) => {
      setSelectedFile(file);
      setUploadingRecording(true);
    },
    onFileLoaded: setSelectedFile,
    onSuccess: onSuccessUploading,
    onCloseModal: () => setUploadingRecording(false),
  };

  const onUploadRecording = () => {
    uploadRecording(id, uploadEvents);
  };

  const onEditRecording = () => {
    const record = sessionRecording?.file ?? sessionRecording ?? selectedFile;
    editRecording(id, record, onSuccessUploading);
  };

  const onRemoveRecording = () => {
    removeRecording(id, onSuccessUploading);
  };

  const onAbortUpload = () => {
    setUploadingRecording(false);
    selectedFile?.abort?.();
  };

  const getProgress = () => {
    if (!selectedFile) return DEFAULT_PROGRESS;
    const { progress = 0 } = selectedFile;
    return progress + DEFAULT_PROGRESS;
  };

  useEffect(() => {
    if (recording) {
      setVideoPlayerClass(isExpanded ? 'video-expanded' : 'video-compact');
    }
  }, [recording, isExpanded]);

  useEffect(() => {
    if (sessionRecording?.uploading) {
      setUploadingRecording(true);
      setSelectedFile(sessionRecording.file);
    }
  }, [sessionRecording]);

  useEffect(() => {
    if (recording?.isDownloadable || recording?.textTranscriptUrl || recording?.slidesUrl) {
      const downloadItem = (url: string, label: string) => (
        <a
          className='bs4-dropdown-item'
          href={url}
          rel='noreferrer'
          target='_blank'
          download
        >
          {label}
        </a>
      );
      const options: NvDropdownOption[] = [
        {
          type: 'header',
          title: t.FILE_UPLOAD.DOWNLOAD(),
        },
      ];
      if (recording.isDownloadable && recording.url) {
        options.push({
          type: 'custom',
          customItem: downloadItem(recording.url, t.LECTURE_VIDEO.VIDEO()),
        });
      }
      if (recording.textTranscriptUrl) {
        options.push({
          type: 'custom',
          customItem: downloadItem(recording.textTranscriptUrl, t.LECTURE_VIDEO.TRANSCRIPTS()),
        });
      }
      if (recording.slidesUrl) {
        options.push({
          type: 'custom',
          customItem: downloadItem(recording.slidesUrl, t.LECTURE_VIDEO.SLIDES()),
        });
      }
      setDownloadOptions(options);
    }
  }, [recording]);

  const getAlignClass = useCallback(() => {
    if (isExpanded) return 'align-self-start';
    if (shouldShowSessionStatus) return 'mt-2';
    return 'align-self-end';
  }, [isExpanded, shouldShowSessionStatus]);

  const countLabelClassName = `label justify-content-center
  ${isAdmin && totalRegisteredUsersCount > 0
    ? 'cursor-pointer text-primary'
    : 'text-gray-2 disabled'}
  ${getAlignClass()}`;


  return (
    <div className={`session-card d-flex p-4 mt-5 ${videoPlayerClass} ${!startTime ? 'border-dashed' : ''}`} css={styles}>
      {recording && (
        <div className='session-recording d-flex align-items-center'>
          <div className='d-flex recording-header justify-content-between mb-2 w-100'>
            <div className='gray-2'>{recording.name}</div>
            <div onClick={onCloseVideo} data-qa={config.pendo.liveEvent.liveSesssionCard.closeRecording}>
              <i className='icon-close' />
            </div>
          </div>
          <NvVideoPreview
            ref={playerInstanceRef}
            file={{ ...recording, hasBeenTranscoded: true }}
            autoStart={isExpanded}
            {...eventHandlers}
          />
        </div>
      )}
      {uploadingRecording && (
        <div className='session-recording uploader d-flex align-items-center'>
          <NvFileUploadProgress
            hasUploadPercentage
            uploadInProgress={selectedFile?.uploadInProgress ?? true}
            showFileName={false}
            fileUploading={{ ...selectedFile, progress: getProgress() }}
            abortUpload={onAbortUpload}
            className='w-100 file-upload-progress'
          />
        </div>
      )}
      <div className='session-body d-flex'>
        <LiveSessionCardTitle
          title={title}
          status={cardBadge}
          allowUserRegistration={isRegistrationEnabled}
        />
        <div
          className='session-description gray-1 mt-2 mb-2'
          dangerouslySetInnerHTML={{
            __html: description,
          }}
        />
        <LiveSessionTiming
          duration={duration}
          startTime={startTime}
          timezone={localTimeZone}
        />
      </div>
      {!!downloadOptions.length && (
        <NvDropdown
          buttonStyle={NvDropdownButtonStyle.ICON}
          items={downloadOptions}
          align={NvDropdownAlign.RIGHT}
          iconClass='icon-small icon-download text-gray-1 '
          tooltip={t.FILE_UPLOAD.DOWNLOAD()}
        />
      )}

      {(shouldShowSessionStatus || canShowRegisteredCount) && (
        <div className='session-actions d-flex'>
          {shouldShowSessionStatus && (
            <>
              <LiveSessionCardAction
                status={sessionAction.status}
                disabled={sessionAction.disabled || registrationRequesting}
                joinUrl={session.joinUrl}
                onClick={onCardActionClicked}
                requesting={registrationRequesting}
              />
              <LiveSessionCardState
                status={sessionStatus}
                allowUserRegistration={isRegistrationEnabled && !isAdmin} // unregister Action not for admins
                onClick={() => toggleRegistration(false)}
                requesting={registrationRequesting}
              />
            </>
          )}
          {canShowRegisteredCount && (
            <ClickableContainer
              className={countLabelClassName}
              onClick={() => setShowRegisteredModal(true)}
            >
              {`${t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.COUNT(
                totalRegisteredUsersCount > 0 ? totalRegisteredUsersCount : 0,
              )}
              ${hasReachedLimit
                ? t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.LIMIT.FULL()
                : ''}`}
            </ClickableContainer>
          )}
        </div>
      )}
      {isEdit && (
        <LiveSessionCardEditOptions
          disabled={!sessionSettings.ended}
          hasRecording={!!recording}
          uploadAttendeeList={() => uploadAttendeeList(id)}
          uploadRecording={onUploadRecording}
          editRecording={onEditRecording}
          removeRecording={onRemoveRecording}
          delete={() => deleteSession(id, onSuccessDeletion)}
        />
      )}
      <RegisteredDetailsModal
        onClose={() => setShowRegisteredModal(false)}
        catalogId={catalogId}
        registeredSession={session}
        show={showRegisteredModal}
      />
    </div>
  );
};

export default LiveSessionCard;
